import React from "react";

import type { ProductCardDataType } from "./components/types";
import { CarouselVerticalProductCard } from "./variants/CarouselVerticalProductCard";
import { StandardVerticalProductCard } from "./variants/StandardVerticalProductCard";

type Props = {
  variant: "standard" | "carousel";
  product: ProductCardDataType;
  onButtonClick?: () => void;
  onShadeSelectorClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onWishlistToggle?: (productCardData: ProductCardDataType) => void;
  onCardClick?: (
    event: React.MouseEvent<HTMLAnchorElement | HTMLDivElement>
  ) => void;
  buttonVariant?: "primary" | "secondary";
  disableLink?: boolean;
};

/**
 * VerticalProductCard - The canonical vertical product card component
 *
 * This is the new vertical product card component and should be the only one used
 * across the application. It provides a flexible variant system that allows you to
 * control the appearance and behavior through the `variant` prop.
 *
 * ## Variant System
 * You can control which variant is rendered using the `variant` prop. Current variants:
 * - `standard`: Default product card layout
 * - `carousel`: Product card optimized for carousel/slider contexts
 *
 * ## Adding New Variants
 * New variants can be added in the `variants/` folder, but this should only be done
 * when necessary and with design approval. Each variant should:
 * - Follow the same prop interface as existing variants
 * - Be purely presentational
 * - Use the shared components from the `components/` folder
 *
 * ## Presentational Component
 * This component is purely presentational. All business logic, data fetching,
 * state management, and event handling must be handled by the parent component.
 *
 * @param variant - Controls which product card variant to render
 * @param product - Pre-transformed product data (ProductCardDataType)
 * @param onButtonClick - Callback fired when the main button is clicked
 * @param onShadeSelectorClick - Callback fired when shade selector is clicked
 * @param onWishlistToggle - Callback fired when wishlist button is toggled
 * @param onCardClick - Callback fired when the card container is clicked
 * @param disableLink - Prevents navigation on card click
 * @param buttonVariant - Visual style variant for the main button
 *
 * @example
 * ```tsx
 * // Data must be pre-transformed using toProductCardData()
 * const transformedProduct = toProductCardData(rawProductData);
 *
 * <VerticalProductCard
 *   variant="standard"
 *   product={transformedProduct}
 *   onButtonClick={() => addToCart(transformedProduct.id)}
 *   onWishlistToggle={(data) => toggleWishlist(data.id)}
 * />
 * ```
 */
export const VerticalProductCard = React.forwardRef<
  HTMLDivElement | HTMLAnchorElement,
  Props
>(
  (
    {
      variant = "standard",
      product,
      onButtonClick,
      onShadeSelectorClick,
      onWishlistToggle,
      onCardClick,
      buttonVariant,
      disableLink
    },
    ref
  ) => {
    if (variant === "carousel") {
      return (
        <CarouselVerticalProductCard
          productCardData={product}
          onButtonClick={onButtonClick}
          onShadeSelectorClick={onShadeSelectorClick}
          onWishlistToggle={onWishlistToggle}
          onCardClick={onCardClick}
          buttonVariant={buttonVariant}
          disableLink={disableLink}
          ref={ref}
        />
      );
    }

    return (
      <StandardVerticalProductCard
        productCardData={product}
        onButtonClick={onButtonClick}
        onShadeSelectorClick={onShadeSelectorClick}
        onWishlistToggle={onWishlistToggle}
        onCardClick={onCardClick}
        buttonVariant={buttonVariant}
        disableLink={disableLink}
        ref={ref}
      />
    );
  }
);
